import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

export interface PartnershipConflict {
  id?: number;
  schueler1_id: string;
  schueler2_id: string;
  gewichtung: number;
  schueler1_vorname?: string;
  schueler1_name?: string;
  schueler2_vorname?: string;
  schueler2_name?: string;
}

export interface AttendanceUpdate {
  type: 'attendance_added';
  teacher: string;
  sessionId: number;
  student: {
    unid: string;
    name: string;
    vorname: string;
    klasse: string;
    memo?: string | null;
  };
  negativePartnershipConflicts?: PartnershipConflict[];
  timestamp: string;
}

export interface PanelSwitchUpdate {
  type: 'panel_switch';
  action: 'switch_to_panel_b';
  teacher: string;
  sessionId: number;
  student: {
    unid: string;
    name: string;
    vorname: string;
    klasse: string;
  };
  reason: string;
  timestamp: string;
}

export interface ToastDismissUpdate {
  type: 'toast_dismiss';
  sessionId: number;
  groupId: number;
  action: 'positive' | 'negative' | 'ok' | 'extend';
  dismissedBy: string;
  timestamp: string;
}

interface UseAttendanceUpdatesOptions {
  enabled?: boolean;
  onUpdate?: (update: AttendanceUpdate) => void;
  onPanelSwitch?: (update: PanelSwitchUpdate) => void;
  onToastDismiss?: (update: ToastDismissUpdate) => void;
  onNegativePartnershipWarning?: (conflicts: PartnershipConflict[], studentId: string) => void;
}

const INITIAL_RECONNECT_DELAY = 5000;
const MAX_RECONNECT_DELAY = 60000;
const RATE_LIMIT_MAX_DELAY = 120000;
const MAX_RECONNECT_ATTEMPTS = 5;
const POLLING_INTERVAL = 15000;

export const useAttendanceUpdates = (options: UseAttendanceUpdatesOptions = {}) => {
  const { enabled = true, onUpdate, onPanelSwitch, onToastDismiss, onNegativePartnershipWarning } = options;
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const isRateLimitedRef = useRef(false);
  const queryClient = useQueryClient();
  const processedUpdatesRef = useRef<Set<string>>(new Set());
  const [connectionMode, setConnectionMode] = useState<'sse' | 'polling'>('sse');

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('⏹️ Polling fallback stopped');
    }
    setConnectionMode('sse');
  }, []);

  const startPolling = useCallback((reason?: string) => {
    if (pollingIntervalRef.current) {
      return;
    }

    console.warn('📡 Switching to polling fallback', reason);
    setConnectionMode('polling');

    queryClient.invalidateQueries(['lernbuero-attendance']);
    queryClient.invalidateQueries(['lernbuero-sessions']);

    pollingIntervalRef.current = window.setInterval(() => {
      console.log('⏱️ Polling attendance updates due to SSE unavailability');
      queryClient.invalidateQueries(['lernbuero-attendance']);
      queryClient.invalidateQueries(['lernbuero-sessions']);
    }, POLLING_INTERVAL);
  }, [queryClient]);

  const resetReconnectState = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
    isRateLimitedRef.current = false;
    clearReconnectTimeout();
  }, [clearReconnectTimeout]);

  const isRateLimitError = useCallback((error: unknown) => {
    if (!error) {
      return false;
    }

    const status = (error as any)?.status ?? (error as any)?.statusCode;
    if (status === 429) {
      return true;
    }

    const message = (error as any)?.message ?? (error as ErrorEvent)?.message;
    if (typeof message === 'string' && message.includes('429')) {
      return true;
    }

    const target = (error as any)?.target ?? (error as ErrorEvent)?.currentTarget;
    if (target && typeof (target as EventSource).readyState === 'number') {
      const readyState = (target as EventSource).readyState;
      if (readyState === EventSource.CLOSED && reconnectAttemptsRef.current > 0) {
        return isRateLimitedRef.current;
      }
    }

    return false;
  }, []);

  const connect = useCallback(() => {
    if (!enabled) {
      console.log('🚫 SSE connection disabled');
      return;
    }

    // Prevent multiple connections
    if (eventSourceRef.current) {
      console.log('🚫 SSE connection already exists, skipping new connection');
      return;
    }

    console.log('🔗 Attempting to establish SSE connection...');
    clearReconnectTimeout();

    try {
      // Create EventSource with cookie authentication
      // EventSource automatically sends cookies with withCredentials
      // Automatische Erkennung der Backend-URL basierend auf der Umgebung
      let backendUrl;
      if (import.meta.env.DEV) {
        // Development: Verwende Vite-Proxy für CORS-freie Anfragen
        backendUrl = window.location.origin; // Vite-Proxy läuft auf demselben Port
      } else {
        // Production: Verwende die aktuelle Domain
        backendUrl = window.location.origin;
      }
      
      const sseUrl = `${backendUrl}/api/events/attendance-updates`;
      console.log('🌐 Creating EventSource with URL:', sseUrl);
      
      // EventSource with credentials to send cookies
      const eventSource = new EventSource(sseUrl, { withCredentials: true });

      eventSource.onopen = () => {
        console.log('🔗 SSE connection established for attendance updates');
        resetReconnectState();
        stopPolling();
      };

      eventSource.onmessage = (event) => {
        console.log('📨 Raw SSE message received:', event.data);
        
        try {
          const data = JSON.parse(event.data);
          console.log('📱 Parsed SSE message:', data);
          
          if (data.type === 'connected') {
            console.log('✅ Connected to attendance updates stream with client ID:', data.clientId);
            return;
          }

          if (data.type === 'attendance_added') {
            console.log('🎉 Received attendance update:', data);
            
            // Create unique identifier for this update to prevent duplicates
            const updateId = `${data.student.unid}-${data.sessionId}-${data.timestamp}`;
            
            if (processedUpdatesRef.current.has(updateId)) {
              console.log('🚫 Duplicate attendance update detected, skipping:', updateId);
              return;
            }
            
            // Mark this update as processed
            processedUpdatesRef.current.add(updateId);
            
            // Clean up old entries (keep only last 100)
            if (processedUpdatesRef.current.size > 100) {
              const entries = Array.from(processedUpdatesRef.current);
              processedUpdatesRef.current = new Set(entries.slice(-50));
            }
            
            // Call custom callback if provided
            if (onUpdate) {
              onUpdate(data);
            }

            if (data.negativePartnershipConflicts && data.negativePartnershipConflicts.length > 0) {
              console.warn('Negative Lernbeziehungen erkannt:', data.negativePartnershipConflicts);
              
              // Call the callback to show the warning modal
              if (onNegativePartnershipWarning) {
                onNegativePartnershipWarning(data.negativePartnershipConflicts, data.student.unid);
              }
            }

            // Invalidate and refetch attendance data for the specific session
            console.log('🔄 Invalidating React Query cache for session:', data.sessionId);
            queryClient.invalidateQueries(['lernbuero-attendance', data.sessionId]);
            queryClient.invalidateQueries(['lernbuero-attendance']);
            
            // Also invalidate any other related queries that might need updating
            queryClient.invalidateQueries(['lernbuero-sessions']);
            
            // Only show success toast if there are no conflicts
            // If there are conflicts, the warning modal will handle the flow
            if (!data.negativePartnershipConflicts || data.negativePartnershipConflicts.length === 0) {
              // Show memo-aware toast notification
              import('../utils/memoToast').then(({ createAttendanceToast }) => {
                createAttendanceToast(
                  data.student.name, 
                  data.student.vorname, 
                  data.student.memo,
                  {
                    duration: 6000,
                    position: 'top-right'
                  }
                );
              }).catch(() => {
                // Fallback if memoToast is not available
                import('react-hot-toast').then(({ toast }) => {
                  toast.success(
                    `${data.student.vorname} ${data.student.name} wurde zur Anwesenheit hinzugefügt`,
                    {
                      duration: 6000,
                      position: 'top-right'
                    }
                  );
                }).catch(() => {
                  console.log(`✅ ${data.student.vorname} ${data.student.name} wurde zur Anwesenheit hinzugefügt`);
                });
              });
            }
          } else if (data.type === 'panel_switch') {
            console.log('🔄 Received panel switch update:', data);
            
            // Create unique identifier for this panel switch update
            const panelSwitchId = `panel-${data.cardId || data.student?.unid || 'unknown'}-${data.timestamp}`;
            
            if (processedUpdatesRef.current.has(panelSwitchId)) {
              console.log('🚫 Duplicate panel switch update detected, skipping:', panelSwitchId);
              return;
            }
            
            // Mark this update as processed
            processedUpdatesRef.current.add(panelSwitchId);
            
            // Call custom callback if provided
            if (onPanelSwitch) {
              onPanelSwitch(data);
            }
            
            // Show toast notification for panel switch
            import('react-hot-toast').then(({ toast }) => {
              if (data.action === 'switch_to_panel_b') {
                (toast as any).info(
                  `Automatischer Wechsel zu Panel B für ${data.student?.vorname || 'Schüler'} ${data.student?.name || 'unbekannt'} (bereits anwesend)`,
                  {
                    duration: 6000,
                    position: 'top-right'
                  }
                );
              } else if (data.action === 'switch_to_panel_a') {
                (toast as any).info(
                  `Automatischer Wechsel zu Panel A (Karte entfernt)`,
                  {
                    duration: 6000,
                    position: 'top-right'
                  }
                );
              }
            }).catch(() => {
              // Fallback if react-hot-toast is not available
              if (data.action === 'switch_to_panel_b') {
                console.log(`🔄 Automatischer Wechsel zu Panel B für ${data.student?.vorname || 'Schüler'} ${data.student?.name || 'unbekannt'}`);
              } else if (data.action === 'switch_to_panel_a') {
                console.log(`🔄 Automatischer Wechsel zu Panel A (Karte entfernt)`);
              }
            });
          } else if (data.type === 'toast_dismiss') {
            console.log('🍞 Received toast dismiss update:', data);
            
            // Create unique identifier for this toast dismiss update
            const toastDismissId = `toast-dismiss-${data.groupId}-${data.sessionId}-${data.timestamp}`;
            
            if (processedUpdatesRef.current.has(toastDismissId)) {
              console.log('🚫 Duplicate toast dismiss update detected, skipping:', toastDismissId);
              return;
            }
            
            // Mark this update as processed
            processedUpdatesRef.current.add(toastDismissId);
            
            // Call custom callback if provided
            if (onToastDismiss) {
              onToastDismiss(data);
            }
            
            console.log(`🍞 Toast dismiss for group ${data.groupId} in session ${data.sessionId} by ${data.dismissedBy}`);
          }
        } catch (error) {
          console.error('❌ Error parsing SSE message:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('❌ SSE connection error:', error);

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        } else {
          eventSource.close();
        }

        const hitRateLimit = isRateLimitError(error);
        if (hitRateLimit) {
          isRateLimitedRef.current = true;
        }

        reconnectAttemptsRef.current += 1;

        const growthFactor = hitRateLimit ? 2 : 1.5;
        const maxDelay = hitRateLimit ? RATE_LIMIT_MAX_DELAY : MAX_RECONNECT_DELAY;
        const nextDelay = Math.min(reconnectDelayRef.current * growthFactor, maxDelay);
        reconnectDelayRef.current = Math.max(INITIAL_RECONNECT_DELAY, nextDelay);
        const jitter = Math.random() * 1000;
        const finalDelay = reconnectDelayRef.current + jitter;

        if ((hitRateLimit || reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) && !pollingIntervalRef.current) {
          startPolling(hitRateLimit ? 'Server responded with 429' : 'Max reconnect attempts reached');
        }

        if (enabled) {
          console.log(`🔄 Attempting to reconnect SSE in ${Math.round(finalDelay)}ms (attempt ${reconnectAttemptsRef.current})`);
          clearReconnectTimeout();
          reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null;
            if (enabled) {
              connect();
            }
          }, finalDelay);
        }
      };

      eventSourceRef.current = eventSource;
      console.log('✅ EventSource created and stored in ref');
    } catch (error) {
      console.error('❌ Error creating SSE connection:', error);
    }
  }, [
    clearReconnectTimeout,
    enabled,
    isRateLimitError,
    onNegativePartnershipWarning,
    onPanelSwitch,
    onToastDismiss,
    onUpdate,
    queryClient,
    resetReconnectState,
    startPolling,
    stopPolling
  ]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      console.log('🔌 SSE connection closed');
    }

    stopPolling();
    resetReconnectState();
  }, [resetReconnectState, stopPolling]);

  useEffect(() => {
    console.log('🔄 useAttendanceUpdates useEffect triggered:', { enabled, hasConnection: !!eventSourceRef.current });
    
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      console.log('🧹 useAttendanceUpdates cleanup triggered');
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected: !!eventSourceRef.current,
    mode: connectionMode,
    connect,
    disconnect
  };
};
